import axios, { AxiosInstance, AxiosError } from 'axios';
import * as Yup from 'yup';
import jwt_decode from 'jwt-decode';
import * as auth from '../utils/auth';
import { HttpStatusCode } from '@lendica/api';
import { matchBackend, User } from '@lendica/utils';

let axiosInstance: AxiosInstance;

export const emailOrPhoneSchema = Yup.object({
    emailOrPhone: Yup.string()
        .required('Required')
        .test(
            'email-or-phone',
            'Enter a valid email or phone number',
            val => !!val && !!auth.detectChannel(val)
        ),
});

export const phoneSchema = Yup.object({
    phone: Yup.string()
        .test('len', 'Please provide a valid phone number', val => val?.length === 10)
        .required('Phone is required'),
});

export const otpSchema = Yup.object({
    // otpCode: Yup.string()
    //     .required('Enter 4 digits')
    //     .matches(/^\d{4}$/, 'Digits only (4)'),
    otpCode: Yup.string()
        .test(
            'len',
            'Please provide a valid one-time password',
            val => val!.length! > 2 && val!.length! < 5
        )
        .required('OTP code is required'),
});

export const passwordSchema = Yup.object({
    password: Yup.string().min(8, 'At least 8 characters').required('Required'),
});

export const xDomainAuthUrl = /.app.golendica.com/gi.test(`${window.location}`)
    ? 'https://accounts.golendica.com'
    : 'https://accounts.app-idev.golendica.com';

export const setAxiosBaseUrl = (baseURL: string) => {
    axiosInstance = axios.create({ baseURL });
};

/* Unauthenticated routes */

export enum ForgotPWActionType {
    NotRegistered,
    NotVerified,
    PendingRegistration,
    Verified,
    Other,
}

export const NextStep = {
    Phone: 'phone',
    CreatePassword: 'create_password',
    ChangePassword: 'change_password',
    Register: 'register',
    ResetPassword: 'reset_password',
    Login: 'login',
    AddLoginMethod: 'add_login_method',
    VerifyExistingEmail: 'verify_existing_email',
    LoginAttemptFail: 'login_attempt_fail',
};

/**
 * Forgot password.
 *
 * @route POST /auth/password/forgot
 * @description User forgot or don't know password, triggers sending a magic link to reset, create, change password or register to the email address based on type of account.
 *
 * @param {string} email - The email to forget the password for.
 *
 * @returns {Promise<{message: string, status: string, action_type: ForgotPWActionType}>} Resolves with message, status, and action type.
 * @throws {ApiError} If the request fails or the email is missing.
 *
 * @example
 * const { message, status, action_type } = await forgotPassword('test@example.com');
 */
export const forgotPassword = async (
    email: string
): Promise<{
    message: string;
    status: string;
    action_type: ForgotPWActionType;
}> => {
    // await new Promise(resolve => setTimeout(resolve, 1000));
    // return {
    //     message: 'Email sent',
    //     status: 'success',
    //     action_type: ForgotPWActionType.Verified,
    // };
    const response = await axiosInstance.post('/auth/password/forgot', { email });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return response.data;
    }
    throw new Error('Unexpected status code');
};

/**
 * Verify email link.
 *
 * @route POST /auth/email/verify
 * @description Verifies an email address using a token from magic link.
 *
 * @param {string} verify_token - The verification token to use.
 *
 * @returns {Promise<{message: string, next_step?: string | null, verification_token?: string}>} Resolves with message and next step.
 * @throws {ApiError} If the request fails or the verification token is invalid or expired.
 *
 * @example
 * const { message, next_step } = await emailVerify('abc123');
 */
export const emailVerify = async (
    verify_token: string
): Promise<{
    message: string;
    next_step?: string | null; // 'phone' | 'reset_password' | 'register' | 'change_password'
    verification_token?: string; // same token in request
}> => {
    // await new Promise(resolve => setTimeout(resolve, 1000));
    // return {
    //     message: 'Email address has been successfully added',
    //     next_step: NextStep.Phone,
    // };
    const response = await axiosInstance.post('/auth/email/verify', { verify_token });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return {
            message: response.data.message || 'Your verification link is invalid or expired.',
        };
    }
    if (response.status === HttpStatusCode.Unauthorized) {
        return {
            message: 'Your verification link is invalid.',
        };
    }
    throw new Error('Unexpected status code');
};

/**
 * Coninue with phone verification after email verification for new account only.
 *
 * @route POST /auth/phone/continuation
 * @description Continues the phone number verification process.
 *
 * @param {string} phone - The phone number to continue the verification process for.
 * @param {string} previous_verification_token - The previous verification token to use.
 *
 * @returns {Promise<{message: string, verify_token?: string}>} Resolves with message and verification token.
 * @throws {ApiError} If the request fails or the phone number is missing, invalid, or already in use by another account.
 *
 * @example
 * const { message, verify_token } = await phoneContinuation('+15551234567', 'abc123');
 */
export const phoneContinuation = async (
    phone: string,
    previous_verification_token?: string
): Promise<{
    message: string;
    verify_token?: string; // uuid
}> => {
    // await new Promise(resolve => setTimeout(resolve, 1000));
    // return {
    //     message: 'A one-time code has been sent to your phone successfully.',
    //     verify_token: 'efg234',
    // };

    // return new Promise((resolve, reject) => {
    //     setTimeout(() => {
    //         reject(
    //             new Error('Phone number does not exist or cannot be associated. Please try again.')
    //         );
    //     }, 1000);
    // });
    const response = await axiosInstance.post('/auth/phone/continuation', {
        phone,
        previous_verification_token,
    });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return {
            message:
                response.data.message ||
                'The phone number was missing, invalid, or already in use by another account.',
        };
    }
    if (response.status === HttpStatusCode.Unauthorized) {
        return {
            message: 'Unauthorized request.',
        };
    }
    throw new Error('Unexpected status code');
};

/**
 * Verify phone number.
 *
 * @route POST /auth/phone/verify
 * @description Verifies a phone number using a token and OTP.
 *
 * @param {string} verify_token - The verification token to use.
 * @param {string} otp - The OTP to use.
 *
 * @returns {Promise<{message: string, next_step?: string}>} Resolves with message, and next step.
 * @throws {ApiError} If the request fails or the verification token is invalid, expired, or the OTP is incorrect.
 *
 * @example
 * const { message, next_step } = await phoneVerify('abc123', '1234');
 */
export const phoneVerify = async (
    verify_token: string, // returned from phoneContinuation
    otp: string
): Promise<{
    message: string;
    next_step?: string; // 'create_password' | 'change_password'
}> => {
    // await new Promise(resolve => setTimeout(resolve, 1000));
    // return {
    //     message: 'Phone number has been successfully verified',
    //     next_step: NextStep.CreatePassword,
    // };
    const response = await axiosInstance.post('/auth/phone/verify', { verify_token, otp });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return {
            message: response.data.message || 'Session expired or invalid.',
        };
    }
    if (response.status === HttpStatusCode.Unauthorized) {
        return {
            message: 'Unauthorized request.',
        };
    }
    throw new Error('Unexpected status code');
};

/**
 * Create or reset password.
 *
 * @route POST /auth/password/create
 * @description Creates or resets a password.
 *
 * @param {string} new_password - The new password to create or reset.
 * @param {string} confirm_password - The confirmation password to create or reset.
 * @param {string} verify_token - The verification token to use.
 *
 * @returns {Promise<{message: string}>} Resolves with message.
 * @throws {ApiError} If the request fails or the verification token is invalid, expired, or the passwords do not match.
 *
 * @example
 * const { message } = await passwordCreateOrReset('password123', 'password123', 'abc123');
 */
export const passwordCreateOrReset = async (
    new_password: string,
    confirm_password: string,
    verify_token?: string
): Promise<{
    message: string;
}> => {
    // await new Promise(resolve => setTimeout(resolve, 1000));
    // return {
    //     message: 'Password has been successfully created. Redirecting to login...',
    // };
    const response = await axiosInstance.post('/auth/password/create', {
        new_password,
        confirm_password,
        verify_token,
    });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return {
            message: response.data.message || 'Something went wrong.',
        };
    }
    if (response.status === HttpStatusCode.Unauthorized) {
        return {
            message: 'Unauthorized request.',
        };
    }
    throw new Error('Unexpected status code');
};

export const phone = async (phone_number: string): Promise<any> => {
    const response = await axiosInstance?.post('/auth/phone', { phone_number });
    return response.data;
};

export const phoneWithOTP = async (phone_number: string, otp: string): Promise<any> => {
    const response = await axiosInstance.post('/auth/phone', {
        phone_number,
        otp,
    });
    return response.data;
};

export const emailWithPassword = async (email: string, password: string): Promise<any> => {
    const response = await axiosInstance.post('/auth/login', {
        email,
        password,
    });
    return response.data;
};

/** Sign up with email and password
 * @param email - The email address
 * @param password - The password
 * @param confirm_password - The password confirmation
 * @param first_name - The first name
 * @param last_name - The last name
 * @param company_name - The company name
 * @param phone_number - The phone number (optional)
 * @param utm_meta - The UTM meta data
 * @returns access and refresh tokens
 */
export const signUpWithEmailAndPassword = async (
    email: string,
    password: string,
    confirm_password: string,
    first_name: string,
    last_name: string,
    company_name: string,
    phone_number?: string,
    utm_meta?: SignUpUtmMeta
) => {
    try {
        const response = await axiosInstance.post<SignUpSuccessResponse>(`/auth/email/signup`, {
            email,
            password,
            confirm_password,
            first_name,
            last_name,
            company_name,
            ...(phone_number && { phone_number }),
            ...(utm_meta && { utm_meta }),
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return {
                message: error.response?.data?.message || error.message || 'Something went wrong.',
            };
        }
        throw error;
    }
};

/* End of unauthenticated routes */

/* Authenticated routes */

/**
 * Binds a phone number to a user.
 *
 * @route POST /auth/phone/bind
 * @description Binds a phone number to a user.
 *
 * @param {string} phone - The phone number to bind.
 *
 * @returns {Promise<{message: string}>} Resolves with message.
 * @throws {ApiError} If the request fails or the phone number is missing, invalid, or already in use by another account.
 *
 * @example
 * const { message } = await phoneBind('+15551234567');
 */
export const phoneBind = async (
    phone: string
): Promise<{
    message: string;
}> => {
    const response = await axios.post('/auth/phone/bind', {
        phone,
    });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return {
            message:
                response.data.message ||
                'The phone number was missing, invalid, or already in use by another account.',
        };
    }
    if (response.status === HttpStatusCode.Unauthorized) {
        return {
            message: 'Unauthorized request.',
        };
    }
    throw new Error('Unexpected status code');
};

export const updateUserEmail = async (
    email: string
): Promise<{
    message: string;
}> => {
    const response = await axios.post('/auth/email/update', {
        email,
    });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return {
            message:
                response.data.message ||
                'The email was missing, invalid, or already in use by another account.',
        };
    }
    if (response.status === HttpStatusCode.Unauthorized) {
        return {
            message: 'Unauthorized request.',
        };
    }
    throw new Error('Unexpected status code');
};

export const getUserProfile = async (): Promise<User | null> => {
    try {
        const response = await axios.get('/auth/user_profile');
        if (response.status === HttpStatusCode.Ok) {
            const data = response.data as unknown;
            if (data && typeof data === 'object' && 'user' in (data as Record<string, unknown>)) {
                const user = (data as Record<string, unknown>).user as User | null | undefined;
                return user ?? null;
            }
            return (data as User) ?? null;
        }
        if (response.status === HttpStatusCode.NoContent) {
            return null;
        }
        throw new Error('Unexpected status code');
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
                return null;
            }
        }
        throw error;
    }
};

export const passwordCreateOrResetAuthed = async (
    new_password: string,
    confirm_password: string
): Promise<{
    message: string;
}> => {
    const response = await axios.post('/auth/password/create_by_user', {
        new_password,
        confirm_password,
    });
    if (response.status === HttpStatusCode.Ok) {
        return response.data;
    }
    if (response.status === HttpStatusCode.BadRequest) {
        return {
            message: response.data.message || 'Something went wrong.',
        };
    }
    if (response.status === HttpStatusCode.Unauthorized) {
        return {
            message: 'Unauthorized request.',
        };
    }
    throw new Error('Unexpected status code');
};

export const invalidateToken = async (): Promise<any> => {
    return (
        await axios.post('/auth/logout', {
            refresh_token: auth.refreshToken.get(),
        })
    ).data;
};

export const logout = async (loginURL?: string): Promise<any> => {
    await invalidateToken();
    auth.accessToken.remove();
    auth.refreshToken.remove();
    !!loginURL && window.location.replace(`${loginURL}`);
};

export interface EmailVerificationRequestResponse {
    message?: string;
    email_masked?: string;
    remaining_attempts_today?: number;
    rate_limit_type?: string;
    wait_seconds?: number;
    [key: string]: unknown;
}

export const requestInitialEmailVerification = async () => {
    const response = await axios.post<EmailVerificationRequestResponse>(
        `/auth/email/initial_verification`
    );
    return response.data;
};

/* End of authenticated routes */

export const isTokenExpired = (): boolean => {
    const accessToken = auth.accessToken.get();
    if (!accessToken) return true;
    const user: { exp: number } = jwt_decode(accessToken);
    return user.exp < new Date().valueOf() / 1000;
};

export const setAxiosInterceptors = (
    baseURL: string,
    axiosInstance: AxiosInstance = axios,
    onRefreshFailure = () => {
        window.location.replace(`${window.location.origin}/login`);
    }
) => {
    axiosInstance.interceptors.request.use(async req => {
        const accessToken = auth.accessToken.get();
        const refreshToken = auth.refreshToken.get();

        if (accessToken) {
            req!.headers!.Authorization = `Bearer ${accessToken}`;
        } else {
            return req;
        }

        const user: { exp: number } = jwt_decode(accessToken);

        const isExpired = user.exp < new Date().valueOf() / 1000;

        // If token is not expired, make request
        if (!isExpired) return req;

        // If token is expired, try posting and retreiving new access token
        try {
            const refreshAxiosInstance = axios.create({ baseURL });
            const response = await refreshAxiosInstance.post(`${baseURL}/auth/token/refresh`, {
                refresh: refreshToken,
            });
            if (!!response?.data?.access) {
                auth.accessToken.set(response?.data?.access);
                req!.headers!.Authorization = `Bearer ${response?.data?.access}`;
            } else {
                throw new Error('Access token not returned from refreshing token.');
            }
        } catch (e) {
            console.error(e);
            auth.accessToken.remove();
            auth.refreshToken.remove();
            onRefreshFailure();
        }
        return req;
    });
    // axios.interceptors.response.use(function (response) {
    //     // Any status code that lie within the range of 2xx cause this function to trigger
    //     // Do something with response data
    //     return response;
    //   }, function (error) {
    //     // Any status codes that falls outside the range of 2xx cause this function to trigger
    //     if (error!.response && error!.response!.status === 401) {
    //         // 401 user does not exist, clear tokens
    //         tokens.accessToken.remove();
    //         tokens.refreshToken.remove();
    //         return Promise.resolve('cleared');
    //     }
    //     // Do something with response error
    //     return Promise.reject(error);
    //   });
};

/* V2 API */
const v2BaseURL = matchBackend('v2');

export const InvitationNextStep = {
    SignUp: 'signup',
    LogoutAndSignUp: 'logout_and_signup',
    Login: 'login',
    Accept: 'accept',
    SwitchAccount: 'switch_account',
    Error: 'error',
};

interface VerifyInvitationSuccessResponse {
    status: string;
    data: {
        next_step: string;
        email?: string;
    };
}

interface SuccessResponse {
    status: string;
    message: string;
}
interface ErrorResponse {
    error: string;
}

export interface ExperimentMeta {
    feature_flag_name: string;
    variant: string;
    impression_key?: string | null;
    recorded_at: string;
    config?: Record<string, unknown> | null;
    source: string;
    [key: string]: unknown;
}

export interface SignUpUtmMeta {
    goals?: string | string[];
    experiments?: ExperimentMeta[];
    partner_name?: string;
    utm_id?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    coupon_code?: string;
    [key: string]: unknown;
}

export const verifyInvitation = async (verify_token: string) => {
    try {
        const response = await axios.get<VerifyInvitationSuccessResponse>(
            `${v2BaseURL}/auth/verify_invitation?verify_token=${verify_token}`
        );
        return response.data.data;
    } catch (error) {
        if (axios.isAxiosError<ErrorResponse>(error)) {
            return { message: error.response?.data?.error ?? 'Something went wrong.' };
        }
        throw error;
    }
};

export const acceptInvitation = async (verify_token: string) => {
    try {
        const response = await axios.post<SuccessResponse>(`${v2BaseURL}/auth/accept_invitation`, {
            verify_token,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError<ErrorResponse>(error)) {
            return { message: error.response?.data?.error ?? 'Something went wrong.' };
        }
        throw error;
    }
};

interface SignUpSuccessResponse {
    status: string;
    message: string;
    access: string;
    refresh: string;
}

/**
 * Sign up with invitation
 * @param verify_token - The verification token
 * @param user_data - The user data
 * @returns access and refresh tokens
 */
export const signUpWithInvite = async (
    verify_token: string,
    user_data: {
        first_name: string;
        last_name: string;
        email: string;
        password: string;
        confirm_password: string;
    }
) => {
    try {
        const response = await axios.post<SignUpSuccessResponse>(
            `${v2BaseURL}/auth/signup_with_invitation`,
            {
                verify_token,
                user_data,
            }
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError<ErrorResponse>(error)) {
            return { message: error.response?.data?.error ?? 'Something went wrong.' };
        }
        throw error;
    }
};

export const acceptProductInvitation = async (verify_token: string) => {
    try {
        const response = await axios.get<VerifyInvitationSuccessResponse>(
            `${v2BaseURL}/auth/invitation/product/accept`,
            {
                params: { verify_token },
            }
        );
        return response.data.data;
    } catch (error) {
        if (axios.isAxiosError<ErrorResponse>(error)) {
            return { message: error.response?.data?.error ?? 'Something went wrong.' };
        }
        throw error;
    }
};
